import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo
} from 'react';
import { ApolloQueryResult, useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { useRouter } from 'next/router';
import { NewSection } from '../NewSection';
import { CardsLayout } from '../ui/CardsLayout';
import { ItemCard } from '../ui/ItemCard';
import { SkeletonLoaders } from './SkeletonLoaders';
import { useBookmarks } from '../../hooks/useBookmarks';
import { useClient } from '../../hooks/useClient';
import useScreenSize from '../../hooks/useScreenSize';
import { getSearchVippedItemsUrl } from '../../utils/helpers';
import { SEARCH_RESULT_ROUTES } from '../../utils/helpers/constants';
import { searchVippedItemsQuery } from '../../api/queries';
import { SearchVippedItemsQuery } from '../../graphql/graphql';
import { ItemScopeEnum, ItemSort } from '../../graphql/types';

export interface VippedItemsSectionRef {
  refetch: () => Promise<ApolloQueryResult<SearchVippedItemsQuery>>;
}

interface VippedItemsSectionProps {
  filter: { [key: string]: boolean | string };
  onError: () => void;
}

export const VippedItemsSection = forwardRef<
  VippedItemsSectionRef,
  VippedItemsSectionProps
>(({ filter, onError }, ref) => {
  const { t } = useTranslation();
  const router = useRouter();
  const { isSmall, isLarge } = useScreenSize();
  const isClient = useClient();
  const { isBookmarked } = useBookmarks();

  const isItemsAllPage = router.pathname === SEARCH_RESULT_ROUTES.ITEMS_ALL;
  const searchVippedItemsUrl = getSearchVippedItemsUrl(filter);
  const searchVippedQueryVariables = {
    filter: { ...filter, scope: ItemScopeEnum.VippedPure },
    limit: 8,
    sort: ItemSort.Random
  };

  const {
    loading: searchVippedItemsQueryLoading,
    data: searchVippedItemsQueryData,
    error: searchVippedItemsQueryError,
    refetch: searchVippedItemsQueryRefetch
  } = useQuery<SearchVippedItemsQuery>(searchVippedItemsQuery, {
    variables: searchVippedQueryVariables,
    ssr: false,
    notifyOnNetworkStatusChange: true,
    skip: isItemsAllPage
  });

  useImperativeHandle(ref, () => ({
    refetch: searchVippedItemsQueryRefetch
  }));

  useEffect(() => {
    if (searchVippedItemsQueryError) {
      onError();
    }
  }, [searchVippedItemsQueryError]);

  const vipListLimit = useMemo(() => {
    switch (true) {
      case isLarge:
        return 8;
      case isSmall:
        return 4;
      default:
        return 6;
    }
  }, [isLarge, isSmall]);

  const itemsVipList =
    (searchVippedItemsQueryData?.items &&
      searchVippedItemsQueryData.items
        .slice(0, vipListLimit)
        .map(item => (
          <ItemCard
            key={item.id}
            {...item}
            inBookmarks={isBookmarked(item.id)}
            stat="item_card_search_results_vip"
          />
        ))) ||
    [];

  if (!itemsVipList.length && !searchVippedItemsQueryLoading && isClient) {
    return null;
  }

  return (
    <NewSection
      id="search-page-vipped"
      title={t('sections.vipped.title')}
      url={searchVippedItemsUrl}
      urlTitle={t('sections.residences.show_all')}
      headlineType="h3"
      cypress="vipped-section-title"
    >
      {!searchVippedItemsQueryLoading ? (
        !!itemsVipList.length && <CardsLayout>{itemsVipList}</CardsLayout>
      ) : (
        <SkeletonLoaders noHeader rowCount={2} />
      )}
    </NewSection>
  );
});

VippedItemsSection.displayName = 'VippedItemsSection';
